import { useAppDispatch, useAppSelector } from "app/hooks"
import { useEffect, useRef, useState } from "react"
import {
  createFood,
  deleteFood,
  getExternalSuggestedSearchTexts,
  getMyFoods,
  getSuggestedSearchTexts,
  onExternalKeywordChange,
  onKeywordChange,
  resetSearchedExternalFoods,
  resetSearchedFoods,
  searchEdamaFoods,
  searchFoods,
  setSearchInAddMealPageVisible as setSearchInAddMealPageVisibleAction,
  setSearchFoodOverlayDisplaySection as setSearchFoodOverlayDisplaySectionAction,
  updateFood,
} from "../foodSlice"
import { useFailed, useSuccess } from "features/notification/hooks"
import { ISearchFoodsPayload } from "../foodSlice.types"
import { useGoToMyFoodDetails } from "./myFood/useMyFoodDetails"
import PATHS from "router/paths"
import {
  ICreateMyFoodPayload,
  IUpdateMyFoodPayload,
} from "model/food/types/foodPayload.types"

export const useSearchFoodOverlayDisplaySection = () => {
  const searchFoodOverlayDisplaySection = useAppSelector(
    (state) => state.food.searchFoodOverlayDisplaySection,
  )

  const dispatch = useAppDispatch()

  const setSearchFoodOverlayDisplaySection = (section: string) => {
    dispatch(setSearchFoodOverlayDisplaySectionAction(section))
  }

  return {
    searchFoodOverlayDisplaySection,
    setSearchFoodOverlayDisplaySection,
  }
}

export const useMyFoods = () => {
  const myFoods = useAppSelector((state) => state.food.myFoods)

  const getMyFoodsFailed = useAppSelector(
    (state) => state.food.getMyFoodsFailed,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getMyFoods())
  }, [dispatch])

  useFailed(getMyFoodsFailed)

  return {
    myFoods,
  }
}

export const useSuggestedSearchTexts = () => {
  const keyword = useAppSelector((state) => state.food.keyword)
  const getSuggestedSearchTextsLoading = useAppSelector(
    (state) => state.food.getSuggestedSearchTextsLoading,
  )
  const getSuggestedSearchTextsFailed = useAppSelector(
    (state) => state.food.getSuggestedSearchTextsFailed,
  )
  const suggestedSearchTexts = useAppSelector(
    (state) => state.food.suggestedSearchTexts,
  )

  const isNoSuggestedSearchTexts = useAppSelector(
    (state) => state.food.isNoSuggestedSearchTexts,
  )

  const dispatch = useAppDispatch()
  const timer = useRef<any>(null)

  const handleKeywordChange = (text: string) => {
    clearTimeout(timer.current)

    dispatch(onKeywordChange(text))

    timer.current = setTimeout(() => {
      dispatch(getSuggestedSearchTexts(text))
    }, 500)
  }

  useFailed(getSuggestedSearchTextsFailed)

  return {
    keyword,
    getSuggestedSearchTextsLoading,
    suggestedSearchTexts,
    handleKeywordChange,
    isNoSuggestedSearchTexts,
  }
}

export const useSearchExternalFoods = () => {
  const searchEdamaFoodsResults = useAppSelector(
    (state) => state.food.searchEdamaFoodsResults,
  )
  const searchEdamaFoodsLoading = useAppSelector(
    (state) => state.food.searchEdamaFoodsLoading,
  )
  const searchEdamaFoodsFailed = useAppSelector(
    (state) => state.food.searchEdamaFoodsFailed,
  )

  const noSearchedExternalFoodFounded = useAppSelector(
    (state) => state.food.noSearchedExternalFoodFounded,
  )

  const externalKeyword = useAppSelector((state) => state.food.externalKeyword)
  const externalSearchSession = useAppSelector(
    (state) => state.food.externalSearchSession,
  )

  useFailed(searchEdamaFoodsFailed)

  const dispatch = useAppDispatch()

  const handleSearchExternalFoods = async ({
    nextKeyword,
    forceReset,
  }: {
    nextKeyword: string
    forceReset?: boolean
  }) => {
    if (!nextKeyword) return

    const payload: ISearchFoodsPayload = {
      keyword: nextKeyword,
      session: "",
    }

    if (nextKeyword === externalKeyword && !forceReset) {
      payload.session = externalSearchSession
    }

    if (forceReset) {
      dispatch(resetSearchedExternalFoods())
    }

    return await dispatch(searchEdamaFoods(payload))
  }

  return {
    noSearchedExternalFoodFounded,
    externalSearchSession,
    searchEdamaFoodsResults,
    searchEdamaFoodsLoading,
    handleSearchExternalFoods,
  }
}

export const useExternalSuggestedSearchTexts = () => {
  const externalKeyword = useAppSelector((state) => state.food.externalKeyword)
  const getExternalSuggestedSearchTextsLoading = useAppSelector(
    (state) => state.food.getExternalSuggestedSearchTextsLoading,
  )
  const getExternalSuggestedSearchTextsFailed = useAppSelector(
    (state) => state.food.getExternalSuggestedSearchTextsFailed,
  )
  const externalSuggestedSearchTexts = useAppSelector(
    (state) => state.food.externalSuggestedSearchTexts,
  )

  const isNoExternalSuggestedSearchTexts = useAppSelector(
    (state) => state.food.isNoExternalSuggestedSearchTexts,
  )

  const dispatch = useAppDispatch()
  const timer = useRef<any>(null)

  const handleExternalKeywordChange = (text: string) => {
    clearTimeout(timer.current)

    dispatch(onExternalKeywordChange(text))

    timer.current = setTimeout(() => {
      dispatch(getExternalSuggestedSearchTexts(text))
    }, 500)
  }

  useFailed(getExternalSuggestedSearchTextsFailed)

  return {
    externalKeyword,
    getExternalSuggestedSearchTextsLoading,
    externalSuggestedSearchTexts,
    handleExternalKeywordChange,
    isNoExternalSuggestedSearchTexts,
  }
}

export const useSearchFoods = () => {
  const searchFoodsResults = useAppSelector(
    (state) => state.food.searchFoodsResults,
  )
  const searchFoodsLoading = useAppSelector(
    (state) => state.food.searchFoodsLoading,
  )
  const searchFoodsFailed = useAppSelector(
    (state) => state.food.searchFoodsFailed,
  )

  const noSearchedFoodFounded = useAppSelector(
    (state) => state.food.noSearchedFoodFounded,
  )

  const keyword = useAppSelector((state) => state.food.keyword)
  const searchSession = useAppSelector((state) => state.food.searchSession)

  useFailed(searchFoodsFailed)

  const dispatch = useAppDispatch()

  const handleSearchFoods = async ({
    nextKeyword,
    forceReset,
  }: {
    nextKeyword: string
    forceReset?: boolean
  }) => {
    if (!nextKeyword) return

    const payload: ISearchFoodsPayload = {
      keyword: nextKeyword,
      session: "",
    }

    if (nextKeyword === keyword && !forceReset) {
      payload.session = searchSession
    }

    if (forceReset) {
      dispatch(resetSearchedFoods())
    }

    return await dispatch(searchFoods(payload))
  }

  return {
    noSearchedFoodFounded,
    searchSession,
    searchFoodsResults,
    searchFoodsLoading,
    handleSearchFoods,
  }
}

export const useCreateMyFood = () => {
  const createFoodLoading = useAppSelector(
    (state) => state.food.createFoodLoading,
  )
  const createFoodFailed = useAppSelector(
    (state) => state.food.createFoodFailed,
  )
  const createFoodSuccess = useAppSelector(
    (state) => state.food.createFoodSuccess,
  )

  const dispatch = useAppDispatch()

  const handleCreateFood = (food: ICreateMyFoodPayload) => {
    dispatch(createFood(food))
  }

  useFailed(createFoodFailed)

  const navigateToMyFoodDetails = useGoToMyFoodDetails()


  useSuccess(createFoodSuccess, { message: "" }, () => {
    if (createFoodSuccess) {
      navigateToMyFoodDetails(createFoodSuccess, PATHS.app.myFoods.recipes)
    }
  })

  return {
    createFoodLoading,
    createFoodSuccess,
    handleCreateFood,
  }
}

export const useDeleteFood = (callback?: () => void) => {
  const deleteFoodLoading = useAppSelector(
    (state) => state.food.deleteFoodLoading,
  )
  const deleteFoodSuccess = useAppSelector(
    (state) => state.food.deleteFoodSuccess,
  )

  const deleteFoodFailed = useAppSelector(
    (state) => state.food.deleteFoodFailed,
  )

  const dispatch = useAppDispatch()

  const handleDeleteFood = (id: string) => {
    dispatch(deleteFood(id))
  }

  useFailed(deleteFoodFailed)

  useSuccess(deleteFoodSuccess, { message: "" }, callback)

  return {
    deleteFoodLoading,
    handleDeleteFood,
  }
}

export const useUpdateMyFood = () => {
  const updateFoodLoading = useAppSelector(
    (state) => state.food.updateFoodLoading,
  )
  const updateFoodSuccess = useAppSelector(
    (state) => state.food.updateFoodSuccess,
  )

  const updateFoodFailed = useAppSelector(
    (state) => state.food.updateFoodFailed,
  )

  const dispatch = useAppDispatch()

  const handleUpdateFood = (food: IUpdateMyFoodPayload) => {
    dispatch(updateFood(food))
  }

  useFailed(updateFoodFailed)
  const navigateToMyFoodDetails = useGoToMyFoodDetails()

  useSuccess(updateFoodSuccess, { message: "" }, () => {
    if (updateFoodSuccess) {
      navigateToMyFoodDetails(updateFoodSuccess, PATHS.app.myFoods.recipes)
    }
  })

  useSuccess(updateFoodSuccess, { message: "" })

  return {
    updateFoodLoading,
    updateFoodSuccess,
    handleUpdateFood,
  }
}

// Recent Ingredients hooks
export { useRecentIngredients } from "./useRecentIngredients"
export { useCreateRecentIngredient } from "./useCreateRecentIngredient"
export { useDeleteRecentIngredient } from "./useDeleteRecentIngredient"
